import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Breadcrumbs,
  Autocomplete,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  SETTING_API_02,
  SETTING_API_20,
  SETTING_API_08,
} from "../../api/ApiModel.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import {
  CategoryMessages,
  MainCategoryMessages,
  GenericNameMessages,
  UITextMessages,
  getFieldMaxLengthMessage
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MainCategory {
  maincatcode?: string | number;
  maincatname?: string;
}

interface Category {
  catcode?: string | number;
  catname?: string;
}

interface SubCategory {
  subcatcode?: string | number;
  subcatname?: string;
  catcode?: string | number;
  maincatcode?: string | number;
  descrip?: string;
}

interface FormDetails {
  maincatcode: string | number;
  catcode: string | number;
  subcatname: string;
  descrip: string;
}

interface ErrorState {
  maincatcode?: string;
  catcode?: string;
  subcatname?: string;
  descrip?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedMainCategory?: string | number;
  selectedCategory?: string | number;
  selectedItem?: SubCategory;
  actionText?: string;
  existingSubCategories?: SubCategory[];
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface AutocompleteOption {
  label: string;
  value: string | number;
}

function CreateSubCategory(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  // Location State
  const {
    selectedMainCategory,
    selectedCategory,
    selectedItem,
    actionText,
    existingSubCategories = [],
  } = (location.state as LocationState) || {};

  // Redux User Data
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  // State
  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formDetails, setFormDetails] = useState<FormDetails>({
    maincatcode: "",
    catcode: "",
    subcatname: "",
    descrip: "",
  });

  const [error, setError] = useState<ErrorState>({});
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  const isEditMode = actionText === "Edit" && !!selectedItem;

  // Set initial form details from selectedItem or location state
  useEffect(() => {
    if (isEditMode && selectedItem) {
      setFormDetails({
        maincatcode: selectedItem.maincatcode || "",
        catcode: selectedItem.catcode || "",
        subcatname: selectedItem.subcatname || "",
        descrip: selectedItem.descrip || "",
      });
    } else {
      setFormDetails({
        maincatcode: selectedMainCategory || "",
        catcode: selectedCategory || "",
        subcatname: "",
        descrip: "",
      });
    }
  }, [selectedItem, isEditMode, selectedMainCategory, selectedCategory]);

  // Fetch Main Categories
  const fetchMainCats = async (): Promise<void> => {
    try {
      const result = await externalgetApi(SETTING_API_20, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });
      const list = Array.isArray(result) ? result : (result as any)?.data || [];
      setMainCategories(list);
      setDataLoaded(true);
    } catch (err) {
      console.error(err);
      setAlertType("error");
      setAlertMessage(MainCategoryMessages.error.unexpected);
      setAlertOpen(true);
    }
  };

  // Fetch Categories based on main category
  const fetchCategories = async (maincatcode: string | number): Promise<void> => {
    if (!maincatcode) return;
    
    setIsCategoriesLoading(true);
    try {
      const result = await externalgetApi(SETTING_API_02, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        MAINCATCODE: maincatcode,
        STS: CON_STATUS_ACTIVE,
      });
      const list = Array.isArray(result) ? result : (result as any)?.data || [];
      setCategories(list);
    } catch (err) {
      console.error(err);
      setAlertType("error");
      setAlertMessage(CategoryMessages.error.fetchFailed);
      setAlertOpen(true);
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  // Initial fetch of main categories
  useEffect(() => {
    fetchMainCats();
  }, []);

  // When formDetails.maincatcode changes, fetch categories
  useEffect(() => {
    if (formDetails.maincatcode) {
      fetchCategories(formDetails.maincatcode);
    }
  }, [formDetails.maincatcode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    let newValue = value;

    newValue = newValue.replace(/^\s+/g, "").replace(/\s{2,}/g, " ");

    if (name === "subcatname" && newValue.length > CON_MAX_NAME_LENGTH) {
      setError((prev) => ({
        ...prev,
        subcatname: getFieldMaxLengthMessage("Subcategory Name", CON_MAX_NAME_LENGTH),
      }));
      return;
    } else if (name === "subcatname") {
      setError((prev) => ({ ...prev, subcatname: "" }));
    }

    if (name === "descrip" && newValue.length > CON_MAX_DESCRIPTION_LENGTH) {
      setError((prev) => ({
        ...prev,
        descrip: getFieldMaxLengthMessage(UITextMessages.label.description, CON_MAX_DESCRIPTION_LENGTH),
      }));
      return;
    } else if (name === "descrip") {
      setError((prev) => ({ ...prev, descrip: "" }));
    }

    setFormDetails((prev) => ({ ...prev, [name]: newValue }));
  };

  const showAlert = (msg: string, type: "success" | "error" | "warning"): void => {
    setAlertMessage(msg);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    const { maincatcode, catcode, subcatname, descrip } = formDetails;

    if (!maincatcode || !catcode || !subcatname.trim()) {
      setError({
        maincatcode: !maincatcode ? CategoryMessages.warning.mainCategoryRequired : "",
        catcode: !catcode ? GenericNameMessages.warning.categoryRequired : "",
        subcatname: !subcatname.trim() ? GenericNameMessages.warning.subcategoryRequired : "",
      });
      showAlert(CategoryMessages.warning.requiredFields, "error");
      return;
    }
    
    // DUPLICATE CHECK
    const isDuplicate = existingSubCategories.some((item) => {
      const sameName =
        item.subcatname?.trim().toLowerCase() ===
        subcatname.trim().toLowerCase();

      const sameMainCat = String(item.maincatcode) === String(maincatcode);
      const sameCat = String(item.catcode) === String(catcode);

      if (isEditMode && selectedItem) {
        return (
          sameName &&
          sameMainCat &&
          sameCat &&
          String(item.subcatcode) !== String(selectedItem.subcatcode)
        );
      }

      return sameName && sameMainCat && sameCat;
    });

    if (isDuplicate) {
      showAlert(GenericNameMessages.warning.alreadyExists, "warning");
      return;
    }

    const payload = {
      ...selectedItem,
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      maincatcode,
      catcode,
      subcatname,
      descrip,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
      subcatcode: selectedItem ? selectedItem.subcatcode : 0,
    };

    try {
      setIsLoading(true);
      await externalpostApi(SETTING_API_08, payload);
      showAlert(
        selectedItem ? GenericNameMessages.success.update : GenericNameMessages.success.create,
        "success"
      );
      setTimeout(() => navigate("/vsubcategory"), 1000);
    } catch (err) {
      const error = err as ApiError;
      console.error(error);
      showAlert(error.response?.data?.message || GenericNameMessages.error.unexpected, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = (): void => {
    setError({});
    navigate("/vsubcategory");
  };

  const getMainCategoryOptions = (): AutocompleteOption[] => {
    return mainCategories.map((m) => ({
      label: m.maincatname || "",
      value: m.maincatcode || "",
    }));
  };

  const getCategoryOptions = (): AutocompleteOption[] => {
    return categories.map((c) => ({
      label: c.catname || "",
      value: c.catcode || "",
    }));
  };

  const findSelectedMainCategory = (): AutocompleteOption | undefined => {
    const options = getMainCategoryOptions();
    const selectedValue = String(formDetails.maincatcode);
    return options.find((o) => String(o.value) === selectedValue);
  };

  const findSelectedCategory = (): AutocompleteOption | undefined => {
    const options = getCategoryOptions();
    const selectedValue = String(formDetails.catcode);
    return options.find((o) => String(o.value) === selectedValue);
  };

  const selectedMainCategoryOption = findSelectedMainCategory();
  const selectedCategoryOption = findSelectedCategory();

  return (
    <Box sx={styles.box_add_page_container}>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title={UITextMessages.tooltip.back} arrow>
          <IconButton
            id="btn_backtoview"
            sx={styles.btn_breadcrumb_back}
            onClick={() => navigate("/vsubcategory")}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>
        <Breadcrumbs separator="›" sx={styles.breadcrumb}>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Typography sx={styles.breadcrumb_link_page}>
            {selectedItem ? "Edit Subcategory" : "Create Subcategory"}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.appbar}>
        <Typography sx={styles.appbar_title}>
          {selectedItem ? "Edit Subcategory" : "Create Subcategory"}
        </Typography>
      </Box>

      <Box
        sx={{
          ...styles.add_form_page_container,
          maxWidth: "500px",
          marginTop: "15px",
          padding: 1,
        }}
      >
        <Grid container spacing={2} direction="column">
          <Grid size={{ xs: 12 }}>
            {dataLoaded && (
              <Autocomplete
                key={`maincat-${formDetails.maincatcode}-${mainCategories.length}`}
                id="act_maincatcode"
                options={getMainCategoryOptions()}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => 
                  String(option.value) === String(value?.value)
                }
                disableClearable
                value={selectedMainCategoryOption || null}
                onChange={(_: React.SyntheticEvent, value: AutocompleteOption | null) => {
                  setFormDetails((prev) => ({
                    ...prev,
                    maincatcode: value?.value || "",
                    catcode: "",
                  }));

                  setError((prev) => ({
                    ...prev,
                    maincatcode: "",
                  }));
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_maincatcode"
                      {...restParams}
                      label="Main Category"
                      size="small"
                      fullWidth
                      required
                      error={!!error.maincatcode}
                      helperText={error.maincatcode}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                disabled={isEditMode}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              key={`cat-${formDetails.catcode}-${categories.length}`}
              id="act_catcode"
              options={getCategoryOptions()}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => 
                String(option.value) === String(value?.value)
              }
              value={selectedCategoryOption || null}
              disableClearable
              loading={isCategoriesLoading}
              onChange={(_: React.SyntheticEvent, value: AutocompleteOption | null) => {
                setFormDetails((prev) => ({
                  ...prev,
                  catcode: value?.value || "",
                }));

                setError((prev) => ({
                  ...prev,
                  catcode: "",
                }));
              }}
              renderInput={(params) => {
                const { id, ...restParams } = params;
                return (
                  <TextField
                    id="txt_category"
                    {...restParams}
                    label="Category"
                    size="small"
                    fullWidth
                    required
                    error={!!error.catcode}
                    helperText={error.catcode}
                    FormHelperTextProps={{
                      sx: { fontSize: "12px", color: "red" },
                    }}
                  />
                );
              }}
              disabled={isEditMode}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              id="txt_subcatname"
              label="Subcategory Name"
              fullWidth
              size="small"
              name="subcatname"
              required
              value={formDetails.subcatname}
              onChange={handleInputChange}
              error={!!error.subcatname}
              helperText={error.subcatname}
              FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              id="txt_descrip"
              label={UITextMessages.label.description}
              fullWidth
              size="small"
              name="descrip"
              value={formDetails.descrip}
              onChange={handleInputChange}
              error={!!error.descrip}
              helperText={error.descrip}
              FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.add_form_page_button_box}>
        <Button
          id="btn_cancel"
          variant="outlined"
          sx={styles.btn_secondary}
          onClick={handleCancel}
        >
          {UITextMessages.button.cancel || "Cancel"}
        </Button>

        <Button
          id="btn_confirm"
          variant="contained"
          sx={styles.btn_primary}
          onClick={handleSubmit}
          disabled={isLoading || isCategoriesLoading}
        >
          {selectedItem ? UITextMessages.button.modify : UITextMessages.button.confirm}
        </Button>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
}

export default CreateSubCategory;